import { useEffect, useRef, useState, Fragment } from "react";
import { useTranslation } from "../../locale/useTranslation";
import BossDialog from "../../components/BossDialog";
import moment from "moment";
import { utility } from "../../framework/utility/utilityProvider";
import "../../Styles/your_style.css";

// Beautify property names (e.g. "Value.SlotDateTime" -> "Slot Date Time")
const formatProperty = (prop) => {
    if (!prop) return "";
    let cleanProp = prop;
    if (prop.startsWith("Value.")) {
        cleanProp = prop.substring(6);
    }
    return cleanProp
        .split('.')
        .map(part => {
            if (part.toLowerCase() === 'sid') return 'SID';
            if (part.toLowerCase() === 'csid') return 'CSID';

            let words = part
                .replace(/([A-Z])/g, ' $1') // split camelCase
                .replace(/[_-]/g, ' ')      // split snake_case
                .trim()
                .split(/\s+/);
            return words
                .map(w => w.charAt(0).toUpperCase() + w.slice(1))
                .join(' ');
        })
        .join(' ➔ ')
        .replace(/\bS\s+I\s+D\b/gi, 'SID')
        .replace(/\bC\s+S\s+I\s+D\b/gi, 'CSID');
};

// Component to render inline diffs of nested objects and arrays
const NestedObjectDiff = ({ oldObj, newObj, propName, getValueFn, renderCellPreviewFn }) => {
    const [expandedProps, setExpandedProps] = useState({});

    const toggleExpand = (idx) => {
        setExpandedProps(prev => ({
            ...prev,
            [idx]: !prev[idx]
        }));
    };

    const isArrayOld = Array.isArray(oldObj);
    const isArrayNew = Array.isArray(newObj);

    if (isArrayOld || isArrayNew) {
        const oldArr = isArrayOld ? oldObj : [];
        const newArr = isArrayNew ? newObj : [];

        const getId = (item) => {
            if (!item || typeof item !== 'object') return String(item);
            return item._id || item.SID || item.id || JSON.stringify(item);
        };

        const getItemLabel = (item) => {
            if (!item) return "";
            if (typeof item !== 'object') return String(item);
            return getValueFn ? getValueFn(item, propName) : (item.Description || item.Title || item.Name || item.name || JSON.stringify(item));
        };

        const matchedItems = []; // { type: 'added' | 'removed' | 'modified' | 'unchanged', oldVal, newVal }

        oldArr.forEach(oldItem => {
            const oldId = getId(oldItem);
            const newItem = newArr.find(x => getId(x) === oldId);

            if (!newItem) {
                matchedItems.push({ type: 'removed', oldVal: oldItem, newVal: null });
            } else {
                const oldStr = JSON.stringify(oldItem);
                const newStr = JSON.stringify(newItem);
                if (oldStr === newStr) {
                    matchedItems.push({ type: 'unchanged', oldVal: oldItem, newVal: newItem });
                } else {
                    matchedItems.push({ type: 'modified', oldVal: oldItem, newVal: newItem });
                }
            }
        });

        newArr.forEach(newItem => {
            const newId = getId(newItem);
            const oldItem = oldArr.find(x => getId(x) === newId);
            if (!oldItem) {
                matchedItems.push({ type: 'added', oldVal: null, newVal: newItem });
            }
        });

        if (matchedItems.length === 0) return <div className="audit-empty-val">No items available</div>;

        return (
            <div className="audit-detail-card" style={{ padding: 0, overflow: 'hidden' }}>
                <table className="audit-detail-table">
                    <thead>
                        <tr>
                            <th style={{ width: '25%' }}>Property</th>
                            <th style={{ width: '30%' }}>Old Value (Before)</th>
                            <th style={{ width: '30%' }}>New Value (After)</th>
                            <th style={{ width: '15%' }}>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {matchedItems.map((item, index) => {
                            const getVisibleKeys = (obj) => {
                                if (!obj || typeof obj !== 'object') return [];
                                return Object.keys(obj).filter(k => {
                                    if (k === 'modOn' || k === 'modBy' || k === 'addOn' || k === 'addBy' || k === 'createdBy' || k === 'createdOn') {
                                        return false;
                                    }
                                    const lowerK = k.toLowerCase();
                                    if (lowerK === 'sid' || lowerK === 'csid') {
                                        return true;
                                    }
                                    if (lowerK === 'id' || lowerK.endsWith('id')) {
                                        return false;
                                    }
                                    return true;
                                });
                            };

                            const hasSubObject = (() => {
                                const oldKeys = getVisibleKeys(item.oldVal);
                                const newKeys = getVisibleKeys(item.newVal);
                                const unionKeys = Array.from(new Set([...oldKeys, ...newKeys]));
                                if (unionKeys.length === 0) return false;
                                if (unionKeys.length === 1) {
                                    const singleKey = unionKeys[0].toLowerCase();
                                    if (singleKey === 'description' || singleKey === 'title' || singleKey === 'name' || singleKey === 'fullchannelname') {
                                        return false;
                                    }
                                }
                                return true;
                            })();

                            const isSubExpanded = !!expandedProps[index];
                            const label = item.type === 'removed' ? getItemLabel(item.oldVal) : getItemLabel(item.newVal);

                            return (
                                <Fragment key={index}>
                                    <tr className={index % 2 === 1 ? "audit-detail-row-even" : "audit-detail-row-odd"}>
                                        <td>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <span style={{ fontWeight: 600, color: '#374151' }}>
                                                    {label || `Item ${index + 1}`}
                                                </span>
                                                {hasSubObject && (
                                                    <button
                                                        type="button"
                                                        className={`audit-expand-btn ${isSubExpanded ? 'active' : ''}`}
                                                        onClick={() => toggleExpand(index)}
                                                        style={{ padding: '2px 6px', fontSize: '11px', flexShrink: 0, whiteSpace: 'nowrap' }}
                                                    >
                                                        <i className={`fa ${isSubExpanded ? 'fa-chevron-up' : 'fa-chevron-down'}`} />
                                                        {isSubExpanded ? "Hide" : "Details"}
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            {item.oldVal !== null && item.oldVal !== undefined && item.oldVal !== "" ? (
                                                <span style={{
                                                    textDecoration: item.type === 'removed' || item.type === 'modified' ? 'line-through' : 'none',
                                                    color: item.type === 'removed' || item.type === 'modified' ? '#EF4444' : '#6B7280'
                                                }}>
                                                    {renderCellPreviewFn ? renderCellPreviewFn(item.oldVal, propName) : (typeof item.oldVal === 'object' ? "Object Details" : String(item.oldVal))}
                                                </span>
                                            ) : (
                                                <span className="audit-empty-val">—</span>
                                            )}
                                        </td>
                                        <td>
                                            {item.newVal !== null && item.newVal !== undefined && item.newVal !== "" ? (
                                                <span style={{
                                                    color: item.type === 'added' || item.type === 'modified' ? '#10B981' : '#374151',
                                                    fontWeight: item.type === 'added' || item.type === 'modified' ? 500 : 'normal'
                                                }}>
                                                    {renderCellPreviewFn ? renderCellPreviewFn(item.newVal, propName) : (typeof item.newVal === 'object' ? "Object Details" : String(item.newVal))}
                                                </span>
                                            ) : (
                                                <span className="audit-empty-val">—</span>
                                            )}
                                        </td>
                                        <td>
                                            {item.type === "added" && (
                                                <span className="audit-badge audit-badge-added">+ Added</span>
                                            )}
                                            {item.type === "modified" && (
                                                <span className="audit-badge audit-badge-modified">✎ Modified</span>
                                            )}
                                            {item.type === "removed" && (
                                                <span className="audit-badge audit-badge-removed">- Removed</span>
                                            )}
                                            {item.type === "unchanged" && (
                                                <span className="audit-badge audit-badge-unchanged">Un-Changed</span>
                                            )}
                                        </td>
                                    </tr>
                                    {hasSubObject && isSubExpanded && (
                                        <tr style={{ background: '#F9FAFB' }}>
                                            <td colSpan={4} style={{ padding: '8px 12px 8px 24px', borderBottom: '1px solid #E5E7EB', borderRight: 'none' }}>
                                                <NestedObjectDiff oldObj={item.oldVal} newObj={item.newVal} propName={propName} getValueFn={getValueFn} renderCellPreviewFn={renderCellPreviewFn} />
                                            </td>
                                        </tr>
                                    )}
                                </Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    }

    const o = (typeof oldObj === 'object' && oldObj !== null) ? oldObj : {};
    const n = (typeof newObj === 'object' && newObj !== null) ? newObj : {};

    const allKeys = Array.from(new Set([...Object.keys(o), ...Object.keys(n)])).filter(
        key => {
            if (key === 'modOn' || key === 'modBy' || key === 'addOn' || key === 'addBy' || key === 'createdBy' || key === 'createdOn') {
                return false;
            }
            const lowerKey = key.toLowerCase();
            if (lowerKey === 'sid' || lowerKey === 'csid') {
                return true;
            }
            if (lowerKey === 'id' || lowerKey.endsWith('id')) {
                return false;
            }
            return true;
        }
    );

    if (allKeys.length === 0) return <div className="audit-empty-val">No details available</div>;

    return (
        <div className="audit-detail-card" style={{ padding: 0, overflow: 'hidden' }}>
            <table className="audit-detail-table">
                <thead>
                    <tr>
                        <th style={{ width: '25%' }}>Property</th>
                        <th style={{ width: '30%' }}>Old Value (Before)</th>
                        <th style={{ width: '30%' }}>New Value (After)</th>
                        <th style={{ width: '15%' }}>Status</th>
                    </tr>
                </thead>
                <tbody>
                    {allKeys.map((key, index) => {
                        const oldSubVal = o[key];
                        const newSubVal = n[key];

                        const oldStr = typeof oldSubVal === 'object' && oldSubVal !== null ? (getValueFn ? getValueFn(oldSubVal, key) : JSON.stringify(oldSubVal)) : String(oldSubVal ?? '');
                        const newStr = typeof newSubVal === 'object' && newSubVal !== null ? (getValueFn ? getValueFn(newSubVal, key) : JSON.stringify(newSubVal)) : String(newSubVal ?? '');

                        let changeType = "unchanged";
                        let isChanged = false;
                        const hasNew = newSubVal !== undefined && newSubVal !== null && newSubVal !== "";
                        const hasOld = oldSubVal !== undefined && oldSubVal !== null && oldSubVal !== "";

                        if (hasNew && !hasOld) {
                            changeType = "added";
                            isChanged = true;
                        } else if (!hasNew && hasOld) {
                            changeType = "removed";
                            isChanged = true;
                        } else if (oldStr !== newStr) {
                            changeType = "modified";
                            isChanged = true;
                        }

                        const rowBgClass = index % 2 === 1 ? "audit-detail-row-even" : "audit-detail-row-odd";

                        return (
                            <tr key={key} className={rowBgClass}>
                                <td style={{ fontWeight: 600, color: '#374151' }}>{formatProperty(key)}</td>
                                <td>
                                    {hasOld ? (
                                        <span style={{
                                            textDecoration: isChanged && changeType !== 'added' ? 'line-through' : 'none',
                                            color: isChanged && changeType !== 'added' ? '#EF4444' : '#6B7280'
                                        }}>
                                            {renderCellPreviewFn ? renderCellPreviewFn(oldSubVal, key) : oldStr}
                                        </span>
                                    ) : (
                                        <span className="audit-empty-val">—</span>
                                    )}
                                </td>
                                <td>
                                    {hasNew ? (
                                        <span style={{
                                            color: isChanged && changeType !== 'removed' ? '#10B981' : '#374151',
                                            fontWeight: isChanged && changeType !== 'removed' ? 500 : 'normal'
                                        }}>
                                            {renderCellPreviewFn ? renderCellPreviewFn(newSubVal, key) : newStr}
                                        </span>
                                    ) : (
                                        <span className="audit-empty-val">—</span>
                                    )}
                                </td>
                                <td>
                                    {changeType === "added" && (
                                        <span className="audit-badge audit-badge-added">+ Added</span>
                                    )}
                                    {changeType === "modified" && (
                                        <span className="audit-badge audit-badge-modified">✎ Modified</span>
                                    )}
                                    {changeType === "removed" && (
                                        <span className="audit-badge audit-badge-removed">- Removed</span>
                                    )}
                                    {changeType === "unchanged" && (
                                        <span className="audit-badge audit-badge-unchanged">Un-Changed</span>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export const AuditLogResponse = (props) => {

    const [finalDisplayValue, setFinalDisplayValue] = useState([]);
    const [showAll, setShowAll] = useState(true);
    const [searchText, setSearchText] = useState("");
    const [expandedProps, setExpandedProps] = useState({});

    const tableWrapperRef = useRef(null);
    const [colWidths, setColWidths] = useState([240, 300, 300, 100]);
    const resizeInfo = useRef({ colIndex: -1, startX: 0, startWidth: 0 });

    useEffect(() => {
        if (tableWrapperRef.current) {
            const containerWidth = tableWrapperRef.current.clientWidth;
            if (containerWidth > 100) {
                const w1 = Math.floor(containerWidth * 0.25);
                const w2 = Math.floor(containerWidth * 0.30);
                const w3 = Math.floor(containerWidth * 0.30);
                const w4 = containerWidth - w1 - w2 - w3 - 5;
                setColWidths([w1, w2, w3, w4]);
            }
        }
    }, [props.newValue, props.oldValue]);

    const handleResizeRef = useRef();
    handleResizeRef.current = (e) => {
        if (resizeInfo.current.colIndex === -1) return;
        const diffX = e.clientX - resizeInfo.current.startX;
        const newWidth = Math.max(50, resizeInfo.current.startWidth + diffX);
        setColWidths(prev => {
            const next = [...prev];
            next[resizeInfo.current.colIndex] = newWidth;
            return next;
        });
    };

    const stopResizeRef = useRef();
    stopResizeRef.current = () => {
        resizeInfo.current = { colIndex: -1, startX: 0, startWidth: 0 };
        document.removeEventListener("mousemove", handleResizeGlobal);
        document.removeEventListener("mouseup", stopResizeGlobal);
    };

    const handleResizeGlobal = (e) => handleResizeRef.current(e);
    const stopResizeGlobal = (e) => stopResizeRef.current(e);

    const startResize = (e, index) => {
        e.preventDefault();
        resizeInfo.current = {
            colIndex: index,
            startX: e.clientX,
            startWidth: colWidths[index]
        };
        document.addEventListener("mousemove", handleResizeGlobal);
        document.addEventListener("mouseup", stopResizeGlobal);
    };

    useEffect(() => {
        return () => {
            document.removeEventListener("mousemove", handleResizeGlobal);
            document.removeEventListener("mouseup", stopResizeGlobal);
        };
    }, []);

    const lang = useTranslation();
    const filterSearchRef = useRef([]);
    const title = lang.details_label + (props.details ? " - " + props.details?.Event + " (" + props.details?.User + " - " + props.details?.EntryDateTime + ")" : "");

    useEffect(() => {
        getDipslayValue();
    }, [props.newValue, props.oldValue]);

    const getValueFromObject = (value, key) => {
        if (!value) return "";
        switch (key.toLowerCase()) {
            case 'channel':
            case 'channels':
                return value?.FullChannelName;
            case 'addby':
            case 'modby':
            case 'reportingto':
                return value?.name;
            case 'mediadealrights':
            case 'publishings':
                return (value?.Channel?.map(x => x?.FullChannelName).join(",") || "") + (value?.Platform?.map(x => x?.Description).join(",") || "") + " - " + (value?.PlatformType?.Description || "") + " ( " + moment(new Date(value?.PublishStartDate)).format("YYYY-MM-DD") + " - " + moment(new Date(value?.PublishEndDate)).format("YYYY-MM-DD") + " )";
            case 'synopsis':
                return value?.Synopsis;
            case 'audiotrack':
            case 'localization':
                return value?.Language?.Description;
            case 'segmentdetails':
                return value?.MediaId;
            case 'formatdetail':
                return value?._id;
            case 'style':
                return value?.color;
            case 'roletype':
                return value?.NAME;
            case 'segments':
            case 'segment':
                return value?.SegmentType?.Description;
        }
        return value?.Description ? value?.Description : value?.Title ? value?.Title : value?.Name ? value?.Name : value?.SegmentType?.Title ? value?.SegmentType?.Title : Object.keys(value).length > 0 ? JSON.stringify(value) : "";
    };

    const getValueFromArray = (value, key) => {
        let tempData = [];
        switch (key?.toLowerCase()) {
            default:
                value?.forEach((item) => {
                    let temp = getValueFromObject(item, key);
                    tempData.push(temp);
                });
                return tempData?.join(", ") || "";
        }
    };

    const getValue = (value, key) => {
        if (value === undefined || value === null) {
            return "";
        }
        let type = typeof value;
        switch (type) {
            case "object":
                if (Array.isArray(value)) {
                    return getValueFromArray(value, key);
                } else {
                    return getValueFromObject(value, key);
                }
            case "string":
                return value;
            case "number":
                if (key.toLowerCase().includes("scheduledate") || key.toLowerCase().includes("slotdatetime")) {
                    return utility.convertMilisecondsToDateTimeString(value);
                } else if (key.toLowerCase().includes("periodfrom") || key.toLowerCase().includes("periodto") || key.toLowerCase().includes("date") || key.toLowerCase().includes("modon") || key.toLowerCase().includes("addon") || key.toLowerCase().includes("orderdate") || key.toLowerCase().includes("nominalonair") || key.toLowerCase().includes("exclusivestartdate") || key.toLowerCase().includes("exclusiveenddate") || key.toLowerCase().includes("publishstartdate") || key.toLowerCase().includes("publishenddate")) {
                    return moment(value).format("DD-MM-YYYY HH:mm:ss");
                } else if (key.toLowerCase().includes("time") || key.toLowerCase().includes("duration") || key.toLowerCase().includes("tcin") || key.toLowerCase().includes("tcout")) {
                    return moment.utc(value).format("HH:mm:ss");
                } else {
                    return value;
                }
            case "boolean":
                return value ? "Yes" : "No";
            default:
                return value;
        }
    };

    const getDipslayValue = () => {
        let finalPreareData = [];

        // Union of keys from both oldValue and newValue
        const allKeys = Array.from(new Set([
            ...Object.keys(props?.newValue || {}),
            ...Object.keys(props?.oldValue || {})
        ]));

        allKeys.forEach(key => {
            if (key === "modOn" || key === "modBy" || key === "addOn" || key === "addBy" || key === "createdBy" || key === "createdOn") {
                return;
            }
            const lowerKey = key.toLowerCase();
            if (lowerKey === 'sid' || lowerKey === 'csid') {
                // Keep SID / CSID
            } else if (lowerKey === "id" || lowerKey.endsWith("id")) {
                return;
            }

            let newValue = props.newValue ? props.newValue[key] : undefined;
            let oldValue = (props.oldValue && Object.keys(props.oldValue)?.length > 0) ? props.oldValue[key] : undefined;

            let isChanged = false;
            let changeType = "unchanged";

            const hasNew = newValue !== undefined && newValue !== null && newValue !== "";
            const hasOld = oldValue !== undefined && oldValue !== null && oldValue !== "";

            if (hasNew && !hasOld) {
                changeType = "added";
                isChanged = true;
            } else if (!hasNew && hasOld) {
                changeType = "removed";
                isChanged = true;
            } else if (JSON.stringify(newValue) !== JSON.stringify(oldValue)) {
                changeType = "modified";
                isChanged = true;
            }

            finalPreareData.push({
                Properties: key,
                OldValue: oldValue !== undefined ? oldValue : "",
                NewValue: newValue !== undefined ? newValue : "",
                isChanged,
                changeType
            });
        });

        setFinalDisplayValue(finalPreareData);
        filterSearchRef.current = finalPreareData;
    };

    const getFilteredData = (all = showAll, searchVal = "") => {
        let baseData = filterSearchRef.current || [];
        if (!all) {
            baseData = baseData.filter(item => item.isChanged);
        }
        if (searchVal.trim() !== "") {
            const kw = searchVal.toLowerCase();
            baseData = baseData.filter(item => {
                const propStr = (item.Properties || "").toLowerCase();
                const beautifiedPropStr = formatProperty(item.Properties).toLowerCase();
                const oldStr = typeof item.OldValue === 'object' && item.OldValue !== null ? JSON.stringify(item.OldValue).toLowerCase() : String(item.OldValue || "").toLowerCase();
                const newStr = typeof item.NewValue === 'object' && item.NewValue !== null ? JSON.stringify(item.NewValue).toLowerCase() : String(item.NewValue || "").toLowerCase();

                return propStr.includes(kw) || beautifiedPropStr.includes(kw) || oldStr.includes(kw) || newStr.includes(kw);
            });
        }
        return baseData;
    };

    const onSearch = (value) => {
        setSearchText(value);
        setFinalDisplayValue(getFilteredData(showAll, value));
    };

    const onClickShowChanges = (checked) => {
        setShowAll(checked);
        setFinalDisplayValue(getFilteredData(checked, searchText));
    };

    const toggleExpand = (propName) => {
        setExpandedProps(prev => ({
            ...prev,
            [propName]: !prev[propName]
        }));
    };

    const renderCellPreview = (value, propName) => {
        if (value === undefined || value === null || value === "") {
            return <span className="audit-empty-val">—</span>;
        }

        if (typeof value === "boolean") {
            return value ? (
                <span style={{ color: 'inherit', fontWeight: 500 }}>
                    <i className="fa fa-check mr-1" style={{ color: 'inherit' }} /> Yes
                </span>
            ) : (
                <span style={{ color: 'inherit' }}>
                    <i className="fa fa-times mr-1" style={{ color: 'inherit' }} /> No
                </span>
            );
        }

        const displayVal = getValue(value, propName);
        if (typeof displayVal === "string" && displayVal.trim().startsWith("{") && displayVal.trim().endsWith("}")) {
            try {
                const parsed = JSON.parse(displayVal);
                const label = parsed.Description || parsed.Title || parsed.Name || parsed.name || parsed.FullChannelName || "";
                if (label) {
                    return <span>{label}</span>;
                }
            } catch (e) { }
            return <span style={{ color: 'inherit' }}><i className="fa fa-cubes mr-1" style={{ color: 'inherit' }} /> Object Details</span>;
        }

        return <span>{String(displayVal)}</span>;
    };

    const changeCount = filterSearchRef.current ? filterSearchRef.current.filter(item => item.isChanged).length : 0;

    return (
        <>
            <BossDialog
                title={title}
                onClose={props.onClose}
                width={props.width ?? "1200px"}
                height={props.height ?? "640px"}
            >
                <div className="audit-log-container">
                    <div className="audit-log-toolbar-new">
                        <div className="audit-log-pills">
                            <button
                                type="button"
                                className={`audit-log-pill ${showAll ? 'active' : ''}`}
                                onClick={() => onClickShowChanges(true)}
                            >
                                {lang.show_all_label || "Show All"}
                            </button>
                            <button
                                type="button"
                                className={`audit-log-pill ${!showAll ? 'active' : ''}`}
                                onClick={() => onClickShowChanges(false)}
                            >
                                {lang.changes_only_label || "Changes Only"}
                            </button>
                        </div>

                        <div onClick={() => onClickShowChanges(!showAll)}>
                            <span className="audit-log-badge-changes" title={showAll ? "Click to show changes only" : "Click to show all fields"}>
                                <i className="fa fa-info-circle mr-1" />
                                {changeCount} {changeCount === 1 ? (lang.change_found_label || "change") : (lang.changes_found_label || "changes")}
                            </span>
                        </div>

                        <div className="audit-log-search-wrapper">
                            <i className="fa fa-search audit-log-search-icon" style={{ pointerEvents: 'none' }} />
                            <input
                                type="text"
                                className="audit-log-search-input"
                                style={{ paddingLeft: '38px' }}
                                placeholder={lang.search_button_text || "Search fields..."}
                                value={searchText}
                                onChange={(e) => onSearch(e.target.value)}
                            />
                        </div>
                    </div>

                    <div ref={tableWrapperRef} className="audit-log-table-wrapper">
                        {finalDisplayValue?.length > 0 ? (
                            <table className="audit-log-table" style={{ width: colWidths.reduce((a, b) => a + b, 0) }}>
                                <thead>
                                    <tr>
                                        <th style={{ width: colWidths[0], position: 'relative' }}>
                                            {lang.property_column || "Property"}
                                            <div className="col-resize-handle" onMouseDown={(e) => startResize(e, 0)} />
                                        </th>
                                        <th style={{ width: colWidths[1], position: 'relative' }}>
                                            {lang.old_value_column || "Old Value (Before)"}
                                            <div className="col-resize-handle" onMouseDown={(e) => startResize(e, 1)} />
                                        </th>
                                        <th style={{ width: colWidths[2], position: 'relative' }}>
                                            {lang.new_value_column || "New Value (After)"}
                                            <div className="col-resize-handle" onMouseDown={(e) => startResize(e, 2)} />
                                        </th>
                                        <th style={{ width: colWidths[3], position: 'relative' }}>
                                            {lang.status_column || "Status"}
                                            <div className="col-resize-handle" onMouseDown={(e) => startResize(e, 3)} />
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {finalDisplayValue.map((item, index) => {
                                        const hasObject = (item.OldValue && typeof item.OldValue === 'object') || (item.NewValue && typeof item.NewValue === 'object');
                                        const isExpanded = !!expandedProps[item.Properties];

                                        return (
                                            <Fragment key={index}>
                                                <tr className={index % 2 === 1 ? "audit-log-row-even" : "audit-log-row-odd"}>
                                                    <td>
                                                        <div className="audit-prop-cell">
                                                            {formatProperty(item.Properties)}
                                                            {hasObject && (
                                                                <button
                                                                    type="button"
                                                                    className={`audit-expand-btn ${isExpanded ? 'active' : ''}`}
                                                                    onClick={() => toggleExpand(item.Properties)}
                                                                >
                                                                    <i className={`fa ${isExpanded ? 'fa-chevron-up' : 'fa-chevron-down'}`} />
                                                                    {isExpanded ? "Hide" : "Details"}
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className={item.isChanged ? "audit-val-old-cell" : "audit-val-unchanged-cell"}>
                                                            {renderCellPreview(item.OldValue, item.Properties)}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className={item.isChanged ? "audit-val-new-cell" : "audit-val-unchanged-cell"}>
                                                            {renderCellPreview(item.NewValue, item.Properties)}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        {item.changeType === "added" && (
                                                            <span className="audit-badge audit-badge-added">+ Added</span>
                                                        )}
                                                        {item.changeType === "modified" && (
                                                            <span className="audit-badge audit-badge-modified">✎ Modified</span>
                                                        )}
                                                        {item.changeType === "removed" && (
                                                            <span className="audit-badge audit-badge-removed">- Removed</span>
                                                        )}
                                                        {item.changeType === "unchanged" && (
                                                            <span className="audit-badge audit-badge-unchanged">Un-Changed</span>
                                                        )}
                                                    </td>
                                                </tr>
                                                {hasObject && isExpanded && (
                                                    <tr style={{ background: '#F9FAFB' }}>
                                                        <td colSpan={4} style={{ padding: '0 16px 16px 36px', borderBottom: '1px solid #E5E7EB', borderRight: 'none' }}>
                                                            <NestedObjectDiff oldObj={item.OldValue} newObj={item.NewValue} propName={item.Properties} getValueFn={getValue} renderCellPreviewFn={renderCellPreview} />
                                                        </td>
                                                    </tr>
                                                )}
                                            </Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        ) : (
                            <div className="text-center p-5 text-muted">
                                <i className="fa fa-folder-open-o fa-2x mb-2" style={{ display: 'block', color: '#9CA3AF' }} />
                                {lang.no_record_found_error_message || "No records found"}
                            </div>
                        )}
                    </div>
                </div>
            </BossDialog>
        </>
    );
};